import type { AppRole } from '@empower-safety/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

export type HrisProvider = 'employment_hero' | 'bamboohr' | 'deputy' | 'hibob';

export interface HrisProviderDto {
  provider: HrisProvider;
  display_name: string;
}

export interface ClinicalMatcherDto {
  positions?: string[];
  departments?: string[];
  titlePattern?: string;
}

export interface HrisIntegrationDto {
  id: string;
  organisation_id: string;
  provider: HrisProvider;
  display_name: string | null;
  active: boolean;
  webhook_url: string;
  has_signing_secret: boolean;
  clinical_matcher: ClinicalMatcherDto | null;
  default_app_role: AppRole;
  default_team_ids: string[];
  invite_all: boolean;
  auto_invite: boolean;
  created_at: string;
  verified_at: string | null;
}

export interface UpsertHrisInput {
  provider: HrisProvider;
  display_name?: string;
  signing_secret?: string;
  clinical_matcher?: ClinicalMatcherDto | null;
  default_app_role?: AppRole;
  default_team_ids?: string[];
  invite_all?: boolean;
  auto_invite?: boolean;
  active?: boolean;
}

export function useHrisProviders() {
  const api = useApi();
  return useQuery({
    queryKey: ['hris', 'providers'],
    queryFn: () => api<{ providers: HrisProviderDto[] }>('/hris/providers'),
    staleTime: 5 * 60_000,
  });
}

export function useHrisIntegrations() {
  const api = useApi();
  return useQuery({
    queryKey: ['hris', 'integrations'],
    queryFn: () => api<{ integrations: HrisIntegrationDto[] }>('/hris/integrations'),
    staleTime: 30_000,
  });
}

export function useUpsertHris() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertHrisInput) =>
      api<{ integration: HrisIntegrationDto }>('/hris/integrations', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hris'] }),
  });
}

export function useDeleteHris() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/hris/integrations/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hris'] }),
  });
}
